import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash, ToggleRight, ToggleLeft, Check, Ticket, CurrencyDollar, ClockCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PERIODS = ['monthly', 'quarterly', 'yearly'];
const STATUS_BADGE = {
  trial: 'bg-blue-500/20 text-blue-300',
  active: 'bg-emerald-500/20 text-emerald-300',
  pending_approval: 'bg-amber-500/20 text-amber-300',
  pending_payment: 'bg-amber-500/20 text-amber-300',
  paused: 'bg-orange-500/20 text-orange-300',
  expired: 'bg-red-500/20 text-red-300',
  rejected: 'bg-red-500/20 text-red-300',
  cancelled: 'bg-slate-500/20 text-slate-300',
};

const SubscriptionsView = () => {
  const [tab, setTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [subs, setSubs] = useState([]);
  const [subFilter, setSubFilter] = useState('all');
  const [trialDays, setTrialDays] = useState(14);
  const [trialInput, setTrialInput] = useState(14);
  const [autoApproveFree, setAutoApproveFree] = useState(true);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [showNewCoupon, setShowNewCoupon] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: '', description: '', price: 0, currency: 'INR', billing_period: 'monthly',
    free_trial_days: 0, duration_days: 0, discount_type: 'none', discount_value: 0, max_listings: 0, is_active: true,
  });
  const [couponForm, setCouponForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10, applicable_plan_ids: [],
    valid_from: '', valid_to: '', max_uses: 0, is_active: true,
  });

  const filteredSubs = useMemo(
    () => subs.filter(s => subFilter === 'all' || s.status === subFilter),
    [subs, subFilter]
  );

  const fetchAll = useCallback(async () => {
    try {
      const [p, c, s, st] = await Promise.all([
        axios.get(`${API_URL}/api/admin/subscription-plans`),
        axios.get(`${API_URL}/api/admin/subscription-coupons`),
        axios.get(`${API_URL}/api/admin/seller-subscriptions`),
        axios.get(`${API_URL}/api/admin/subscription-settings`),
      ]);
      setPlans(p.data?.plans || []);
      setCoupons(c.data?.coupons || []);
      setSubs(s.data?.subscriptions || []);
      const td = st.data?.default_trial_days ?? 14;
      setTrialDays(td);
      setTrialInput(td);
      setAutoApproveFree(st.data?.auto_approve_free_plan !== false);
    } catch (e) {
      toast.error('Failed to load subscription data');
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveTrialDays = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/subscription-settings`, { default_trial_days: Number(trialInput) });
      setTrialDays(Number(trialInput));
      toast.success('Trial days updated');
    } catch (e) { toast.error('Failed to update trial'); }
  };

  const toggleAutoApproveFree = async (next) => {
    try {
      await axios.put(`${API_URL}/api/admin/subscription-settings`, { auto_approve_free_plan: next });
      setAutoApproveFree(next);
      toast.success(next ? 'Free plan will auto-activate' : 'Free plan now needs admin approval');
    } catch (e) { toast.error('Failed to update setting'); }
  };

  const createPlan = async () => {
    if (!planForm.name.trim()) return toast.error('Plan name required');
    try {
      await axios.post(`${API_URL}/api/admin/subscription-plans`, { ...planForm, price: Number(planForm.price), free_trial_days: Number(planForm.free_trial_days), duration_days: Number(planForm.duration_days), discount_value: Number(planForm.discount_value), max_listings: Number(planForm.max_listings) });
      setShowNewPlan(false);
      setPlanForm({ name: '', description: '', price: 0, currency: 'INR', billing_period: 'monthly', free_trial_days: 0, duration_days: 0, discount_type: 'none', discount_value: 0, max_listings: 0, is_active: true });
      fetchAll();
      toast.success('Plan created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create plan'); }
  };

  const togglePlan = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/subscription-plans/${id}/toggle`); fetchAll(); }
    catch (e) { toast.error('Toggle failed'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await axios.delete(`${API_URL}/api/admin/subscription-plans/${id}`); fetchAll(); toast.success('Plan deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const createCoupon = async () => {
    if (!couponForm.code.trim()) return toast.error('Coupon code required');
    try {
      await axios.post(`${API_URL}/api/admin/subscription-coupons`, { ...couponForm, discount_value: Number(couponForm.discount_value), max_uses: Number(couponForm.max_uses) });
      setShowNewCoupon(false);
      setCouponForm({ code: '', discount_type: 'percentage', discount_value: 10, applicable_plan_ids: [], valid_from: '', valid_to: '', max_uses: 0, is_active: true });
      fetchAll();
      toast.success('Coupon created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create coupon'); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try { await axios.delete(`${API_URL}/api/admin/subscription-coupons/${id}`); fetchAll(); toast.success('Coupon deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const markPaid = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/seller-subscriptions/${id}/mark-paid`, {}); fetchAll(); toast.success('Marked as paid'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const approveSub = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/seller-subscriptions/${id}/approve`, {}); fetchAll(); toast.success('Subscription approved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const rejectSub = async (id) => {
    const reason = window.prompt('Reason for rejection (shown to seller):', 'Not eligible');
    if (reason === null) return;
    try { await axios.post(`${API_URL}/api/admin/seller-subscriptions/${id}/reject`, { reason }); fetchAll(); toast.success('Subscription rejected'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };
  const editDates = async (s) => {
    const cur = s.period_ends_at ? new Date(s.period_ends_at).toISOString().split('T')[0] : '';
    const nextEnd = window.prompt('New end date (YYYY-MM-DD):', cur);
    if (!nextEnd) return;
    try {
      await axios.put(`${API_URL}/api/admin/seller-subscriptions/${s.sub_id}/dates`, { end_date: new Date(nextEnd + 'T23:59:59Z').toISOString() });
      fetchAll();
      toast.success('End date updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const reactivate = async (id) => {
    const days = window.prompt('Extra days to add (default 30):', '30');
    if (days === null) return;
    try { await axios.post(`${API_URL}/api/admin/seller-subscriptions/${id}/reactivate`, { extra_days: Number(days) || 30 }); fetchAll(); toast.success('Reactivated'); }
    catch (e) { toast.error('Reactivate failed'); }
  };
  const pauseSub = async (id) => {
    if (!window.confirm('Pause subscription? This will also pause all of the seller\'s live listings.')) return;
    try { await axios.post(`${API_URL}/api/admin/seller-subscriptions/${id}/pause`, {}); fetchAll(); toast.success('Paused'); }
    catch (e) { toast.error('Pause failed'); }
  };

  return (
    <div data-testid="admin-subscriptions">
      <p className="text-[#6b7a99] text-sm mb-4">Manage subscription plans, coupons & seller subscriptions</p>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5">
        {[
          { id: 'plans', label: 'Plans', icon: CurrencyDollar },
          { id: 'coupons', label: 'Coupons', icon: Ticket },
          { id: 'subs', label: 'Seller Subs', icon: ClockCounterClockwise },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`subs-subtab-${t.id}`}
              className={`px-3 h-9 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${active ? 'bg-indigo-600 text-white' : 'bg-[#161b26] border border-[#252d3d] text-[#8896b0] hover:text-white'}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Global settings row */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4 mb-5 flex items-center gap-3 flex-wrap" data-testid="trial-days-card">
        <span className="text-xs text-[#8896b0]">Default free trial for new sellers:</span>
        <Input type="number" value={trialInput} onChange={(e) => setTrialInput(e.target.value)} min="0"
          className="w-24 h-8 text-sm bg-[#0f1320] border-[#252d3d] text-white" data-testid="trial-days-input" />
        <span className="text-xs text-[#8896b0]">days</span>
        <Button size="sm" onClick={saveTrialDays} className="bg-indigo-600 hover:bg-indigo-700 h-8 rounded-lg text-xs" data-testid="save-trial-days">Save</Button>
        <span className="text-[10px] text-[#6b7a99]">Current: {trialDays} days</span>
        <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-2" data-testid="auto-approve-free-row">
          <span className="text-xs text-[#8896b0]">Auto-activate Free plan</span>
          <button
            onClick={() => toggleAutoApproveFree(!autoApproveFree)}
            data-testid="auto-approve-free-toggle"
            className={`h-8 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors ${autoApproveFree ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : 'bg-slate-500/10 border-slate-500/30 text-slate-400'}`}>
            {autoApproveFree ? <ToggleRight size={14} weight="fill" /> : <ToggleLeft size={14} />}
            {autoApproveFree ? 'ON' : 'OFF — needs approval'}
          </button>
        </div>
      </div>

      {/* Plans tab */}
      {tab === 'plans' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-white text-sm font-semibold">{plans.length} plan{plans.length !== 1 ? 's' : ''}</h3>
            <Button size="sm" onClick={() => setShowNewPlan(s => !s)} className="bg-indigo-600 hover:bg-indigo-700 h-8 rounded-lg text-xs" data-testid="new-plan-btn">
              <Plus size={12} className="mr-1" /> New Plan
            </Button>
          </div>
          {showNewPlan && (
            <div className="bg-[#161b26] border border-indigo-500/30 rounded-xl p-4 mb-4 space-y-3" data-testid="new-plan-form">
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Plan name" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="plan-name-input" />
                <Input placeholder="Description" value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                <Input type="number" placeholder="Price" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="plan-price-input" />
                <Input placeholder="Currency (e.g. INR)" value={planForm.currency} onChange={(e) => setPlanForm({ ...planForm, currency: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                <Select value={planForm.billing_period} onValueChange={(v) => setPlanForm({ ...planForm, billing_period: v })}>
                  <SelectTrigger className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="plan-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" placeholder="Free trial days" value={planForm.free_trial_days} onChange={(e) => setPlanForm({ ...planForm, free_trial_days: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                <Input type="number" placeholder="Active period days (0 = use billing period)" value={planForm.duration_days} onChange={(e) => setPlanForm({ ...planForm, duration_days: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="plan-duration-days-input" />
                <Select value={planForm.discount_type} onValueChange={(v) => setPlanForm({ ...planForm, discount_type: v })}>
                  <SelectTrigger className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No discount</SelectItem>
                    <SelectItem value="percentage">% off</SelectItem>
                    <SelectItem value="fixed">Fixed off</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Discount value" value={planForm.discount_value} onChange={(e) => setPlanForm({ ...planForm, discount_value: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                <Input type="number" placeholder="Max listings (0=unlimited)" value={planForm.max_listings} onChange={(e) => setPlanForm({ ...planForm, max_listings: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setShowNewPlan(false)} className="text-[#8896b0] h-8 rounded-lg text-xs">Cancel</Button>
                <Button size="sm" onClick={createPlan} className="bg-emerald-600 hover:bg-emerald-700 h-8 rounded-lg text-xs" data-testid="save-plan-btn">Create Plan</Button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {plans.map(p => (
              <div key={p.plan_id} data-testid={`plan-row-${p.plan_id}`} className="bg-[#161b26] border border-[#252d3d] rounded-xl p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-white text-sm font-medium">{p.name}</div>
                  <div className="text-[10px] text-[#6b7a99]">{p.description || '—'}</div>
                </div>
                <div className="text-xs text-[#8896b0]"><span className="text-emerald-400 font-semibold">{p.price} {p.currency}</span> / {p.billing_period}</div>
                <div className="text-[10px] text-[#6b7a99]">Trial {p.free_trial_days}d · Duration {p.duration_days || '—'}d · Max {p.max_listings || '∞'}</div>
                {p.discount_type !== 'none' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-300">{p.discount_value}{p.discount_type === 'percentage' ? '%' : ''} off</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${p.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>{p.is_active ? 'Active' : 'Inactive'}</span>
                <button onClick={() => togglePlan(p.plan_id)} className="text-[#8896b0] hover:text-white p-1.5" data-testid={`toggle-plan-${p.plan_id}`}>
                  {p.is_active ? <ToggleRight size={20} weight="fill" /> : <ToggleLeft size={20} />}
                </button>
                <button onClick={() => deletePlan(p.plan_id)} className="text-red-400 hover:text-red-300 p-1.5" data-testid={`delete-plan-${p.plan_id}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
            {plans.length === 0 && <div className="text-center text-[#6b7a99] py-8 text-sm">No plans yet. Create one above.</div>}
          </div>
        </div>
      )}

      {/* Coupons tab */}
      {tab === 'coupons' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-white text-sm font-semibold">{coupons.length} coupon{coupons.length !== 1 ? 's' : ''}</h3>
            <Button size="sm" onClick={() => setShowNewCoupon(s => !s)} className="bg-indigo-600 hover:bg-indigo-700 h-8 rounded-lg text-xs" data-testid="new-coupon-btn">
              <Plus size={12} className="mr-1" /> New Coupon
            </Button>
          </div>
          {showNewCoupon && (
            <div className="bg-[#161b26] border border-indigo-500/30 rounded-xl p-4 mb-4 space-y-3" data-testid="new-coupon-form">
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Code (e.g. LAUNCH50)" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="coupon-code-input" />
                <Select value={couponForm.discount_type} onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}>
                  <SelectTrigger className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">% off</SelectItem>
                    <SelectItem value="fixed">Fixed amount off</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Discount value" value={couponForm.discount_value} onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                <Input type="number" placeholder="Max uses (0=unlimited)" value={couponForm.max_uses} onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                <Input type="date" placeholder="Valid from" value={couponForm.valid_from} onChange={(e) => setCouponForm({ ...couponForm, valid_from: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                <Input type="date" placeholder="Valid to" value={couponForm.valid_to} onChange={(e) => setCouponForm({ ...couponForm, valid_to: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setShowNewCoupon(false)} className="text-[#8896b0] h-8 rounded-lg text-xs">Cancel</Button>
                <Button size="sm" onClick={createCoupon} className="bg-emerald-600 hover:bg-emerald-700 h-8 rounded-lg text-xs" data-testid="save-coupon-btn">Create Coupon</Button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {coupons.map(c => (
              <div key={c.coupon_id} data-testid={`coupon-row-${c.coupon_id}`} className="bg-[#161b26] border border-[#252d3d] rounded-xl p-3 flex items-center gap-3 flex-wrap">
                <span className="text-white text-sm font-bold tracking-wider">{c.code}</span>
                <span className="text-xs text-emerald-400">{c.discount_value}{c.discount_type === 'percentage' ? '%' : ''} off</span>
                <span className="text-[10px] text-[#6b7a99]">Used: {c.used_count}{c.max_uses ? `/${c.max_uses}` : ''}</span>
                {(c.valid_from || c.valid_to) && <span className="text-[10px] text-[#6b7a99]">{c.valid_from || '—'} → {c.valid_to || '—'}</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${c.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>{c.is_active ? 'Active' : 'Inactive'}</span>
                <button onClick={() => deleteCoupon(c.coupon_id)} className="ml-auto text-red-400 hover:text-red-300 p-1.5" data-testid={`delete-coupon-${c.coupon_id}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
            {coupons.length === 0 && <div className="text-center text-[#6b7a99] py-8 text-sm">No coupons yet.</div>}
          </div>
        </div>
      )}

      {/* Seller subs tab */}
      {tab === 'subs' && (
        <div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <h3 className="text-white text-sm font-semibold">{subs.length} seller subscription{subs.length !== 1 ? 's' : ''}</h3>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[10px] text-[#6b7a99]">Filter:</span>
              {['all', 'pending_approval', 'active', 'trial', 'expired', 'rejected', 'paused'].map(f => (
                <button key={f} onClick={() => setSubFilter(f)}
                  data-testid={`filter-${f}`}
                  className={`px-2 h-6 rounded text-[10px] font-medium ${subFilter === f ? 'bg-indigo-600 text-white' : 'bg-[#161b26] border border-[#252d3d] text-[#8896b0] hover:text-white'}`}>
                  {f === 'all' ? 'All' : f.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {filteredSubs.map(s => (
              <div key={s.sub_id} data-testid={`sub-row-${s.sub_id}`} className="bg-[#161b26] border border-[#252d3d] rounded-xl p-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-white text-sm font-medium">{s.seller_name || '—'}</div>
                    <div className="text-[10px] text-[#6b7a99]">{s.seller_email || s.seller_id}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_BADGE[s.status] || 'bg-slate-500/20 text-slate-300'}`}>{s.status?.replace('_', ' ')}</span>
                  <div className="text-xs text-[#8896b0]">
                    {s.plan_snapshot?.name ? <>{s.plan_snapshot.name} · {s.plan_snapshot.price} {s.plan_snapshot.currency}/{s.plan_snapshot.billing_period}</> : 'No plan'}
                  </div>
                  {s.applied_coupon_code && <span className="text-[10px] text-pink-300">Coupon: {s.applied_coupon_code}</span>}
                  <div className="text-[10px] text-[#6b7a99] flex flex-col items-end">
                    {(s.period_starts_at || s.trial_starts_at) && (
                      <span>Start: {new Date(s.period_starts_at || s.trial_starts_at).toLocaleDateString()}</span>
                    )}
                    {(s.period_ends_at || s.trial_ends_at) && (
                      <span>End: {new Date(s.period_ends_at || s.trial_ends_at).toLocaleDateString()}</span>
                    )}
                    {s.status === 'pending_payment' && <span>Due: {s.amount_due} {s.plan_snapshot?.currency}</span>}
                  </div>
                </div>
                {s.status === 'rejected' && s.rejection_reason && (
                  <div className="text-[11px] text-red-300 italic mt-1">Rejected: &ldquo;{s.rejection_reason}&rdquo;</div>
                )}
                <div className="flex gap-2 mt-2 justify-end flex-wrap">
                  {s.status === 'pending_approval' && (
                    <>
                      <Button size="sm" onClick={() => approveSub(s.sub_id)} className="bg-emerald-600 hover:bg-emerald-700 h-7 rounded-lg text-[11px]" data-testid={`approve-${s.sub_id}`}>
                        <Check size={11} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" onClick={() => rejectSub(s.sub_id)} variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-7 rounded-lg text-[11px]" data-testid={`reject-${s.sub_id}`}>
                        Reject
                      </Button>
                    </>
                  )}
                  {s.status === 'pending_payment' && (
                    <Button size="sm" onClick={() => markPaid(s.sub_id)} className="bg-emerald-600 hover:bg-emerald-700 h-7 rounded-lg text-[11px]" data-testid={`mark-paid-${s.sub_id}`}>
                      <Check size={11} className="mr-1" /> Mark Paid
                    </Button>
                  )}
                  {(s.status === 'expired' || s.status === 'paused' || s.status === 'cancelled' || s.status === 'rejected') && (
                    <Button size="sm" onClick={() => reactivate(s.sub_id)} className="bg-blue-600 hover:bg-blue-700 h-7 rounded-lg text-[11px]" data-testid={`reactivate-${s.sub_id}`}>
                      Re-activate
                    </Button>
                  )}
                  {(s.status === 'active' || s.status === 'trial' || s.status === 'pending_approval') && (
                    <Button size="sm" variant="outline" onClick={() => editDates(s)} className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 h-7 rounded-lg text-[11px]" data-testid={`edit-dates-${s.sub_id}`}>
                      Edit end date
                    </Button>
                  )}
                  {(s.status === 'active' || s.status === 'trial') && (
                    <Button size="sm" variant="outline" onClick={() => pauseSub(s.sub_id)} className="border-orange-500/30 text-orange-300 hover:bg-orange-500/10 h-7 rounded-lg text-[11px]" data-testid={`pause-sub-${s.sub_id}`}>
                      Pause
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {filteredSubs.length === 0 && (
              <div className="text-center text-[#6b7a99] py-8 text-sm">No seller subscriptions match this filter.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsView;
